import {
  collection, addDoc, getDoc, doc, serverTimestamp,
} from 'firebase/firestore';
import { db, firebaseReady } from './firebase';

const COLLECTION = 'shared_routes';

/**
 * Save a route to Firestore and return its public share ID.
 * Only base waypoints (no loop via/end markers) are stored.
 */
export async function shareRouteToFirestore(route) {
  if (!firebaseReady) throw new Error('Firebase not configured');

  const waypoints = (route.waypoints || [])
    .filter(wp => wp.lat != null && wp.lng != null && !wp.isLoopVia && !wp.isLoopEnd)
    .map(wp => ({ lat: wp.lat, lng: wp.lng, name: wp.name || '' }));

  const ref = await addDoc(collection(db, COLLECTION), {
    name:      route.name || 'Shared Route',
    profile:   route.profile || 'driving',
    waypoints,
    // Persist the already-computed path so viewers see the exact route that was
    // shared, instead of re-routing the waypoints (which can land on a different
    // path — routing falls back across Geoapify/Valhalla/OSRM, and each engine
    // can pick a different road). Stringified: Firestore rejects arrays of arrays,
    // and GeoJSON coordinates are exactly that.
    geometry:  route.geometry ? JSON.stringify(route.geometry) : null,
    // Per-leg breakdown, when known — lets whoever loads this link and edits it
    // re-route a single dragged waypoint instead of the whole path right away.
    // (Won't align for loop routes since loop-via/end points aren't saved above;
    // that's fine, the loader's UPDATE_WAYPOINT falls back to a full recompute.)
    legs:      route.legs ? JSON.stringify(route.legs) : null,
    distance:  route.distance ?? null,
    duration:  route.duration ?? null,
    createdAt: serverTimestamp(),
  });

  return ref.id;
}

/**
 * Load a shared route from Firestore by its ID.
 * Returns a route object ready to dispatch as LOAD_ROUTE, or null if not found.
 */
export async function loadSharedRouteFromFirestore(id) {
  if (!firebaseReady) return null;

  const snap = await getDoc(doc(db, COLLECTION, id));
  if (!snap.exists()) return null;

  const data = snap.data();
  return {
    id:        null,
    name:      data.name || 'Shared Route',
    profile:   data.profile || 'driving',
    waypoints: (data.waypoints || []).map(wp => ({
      lat:  wp.lat,
      lng:  wp.lng,
      name: wp.name || '',
    })),
    // Older shares saved before this field existed fall back to null, which
    // still re-routes as before — nothing to migrate, the geometry was never stored.
    geometry: data.geometry ? JSON.parse(data.geometry) : null,
    legs:     data.legs ? JSON.parse(data.legs) : null,
    distance: data.distance ?? null,
    duration: data.duration ?? null,
  };
}

/** Build a shareable URL from a Firestore share ID */
export function buildFirestoreShareURL(shareId) {
  const url = new URL(window.location.href);
  url.hash = '';
  url.search = '';
  url.searchParams.set('share', shareId);
  return url.toString();
}
